import React from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { ThemedSelect } from '../../themed';

/**
 * RouteSelectionSection Component
 *
 * Displays route selection dropdown (if multiple routes) or static route display (if single route).
 * Forces re-render when language changes to update translated route names.
 *
 * State Ownership:
 * - Reads all props from parent (no local state)
 * - Route state managed by parent component
 *
 * @param {Object} props
 * @param {Array} props.routes - List of available routes
 * @param {string} props.selectedRoute - Currently selected route ID
 * @param {Function} props.onChange - Handler for route change
 * @param {boolean} props.loading - Loading state
 */
const RouteSelectionSection = ({
  routes,
  selectedRoute,
  onChange,
  loading = false
}) => {
  const { t, i18n } = useTranslation();
  const { background, text, cn } = useTheme();

  if (loading) {
    return (
      <div>
        <label className={cn("block text-sm font-medium mb-1", text.primary)}>
          <MapPin className="w-5 h-5 inline-block mr-1 text-blue-500" />
          {t('route')}
        </label>
        <div className={cn("p-2 rounded-md border", background.secondary, text.secondary, "border-gray-200 dark:border-gray-700")}>
          {t('loading')}...
        </div>
      </div>
    );
  }

  // Multiple routes - show dropdown
  if (routes.length > 1) {
    return (
      <div>
        <ThemedSelect
          key={`route-select-${i18n.language}`} // Force re-render when language changes
          label={
            <span>
              <MapPin className="w-5 h-5 inline-block mr-1 text-blue-500" />
              {t('route')}
            </span>
          }
          name="route"
          value={selectedRoute}
          onChange={onChange}
          className="w-full border border-gray-200 dark:border-gray-700"
          placeholder={t('selectARoute')}
          options={routes.map(route => ({
            value: route.id,
            label: route.name
          }))}
        />
      </div>
    );
  }

  // Single route - show static display
  if (routes.length === 1) {
    return (
      <div>
        <label className={cn("block text-sm font-medium mb-1", text.primary)}>
          <MapPin className="w-5 h-5 inline-block mr-1 text-blue-500" />
          {t('route')}
        </label>
        <div className={cn("p-2 rounded-md border", background.secondary, text.primary, "border-gray-200 dark:border-gray-700")}>
          {routes[0].name}
        </div>
      </div>
    );
  }

  // No routes available
  return (
    <div>
      <label className={cn("block text-sm font-medium mb-1", text.primary)}>
        <MapPin className="w-5 h-5 inline-block mr-1 text-blue-500" />
        {t('route')}
      </label>
      <div className={cn("p-2 rounded-md border", background.secondary, text.secondary, "border-gray-200 dark:border-gray-700")}>
        {t('noRoutesAvailable')}
      </div>
    </div>
  );
};


export default React.memo(RouteSelectionSection);
